import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { BiEvent } from '@wix/da-bi/pkg/events';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import IconButtonWithBackground from '@wix/da-ds/pkg/Buttons/IconButton/IconButtonWithBackground';
import EditIcon from '@wix/da-ds/pkg/Icons/24x24/Edit';
import { useEditDeviationClick } from '../../../hooks/useEditDeviationClick';

interface Props {
  deviation: PapiDeviation;
  biData?: BiEvent;
  className?: string;
  variant?: 'ghost' | 'icon' | 'icon-with-background' | 'text';
}

const EditButton: FC<Props> = ({
  deviation,
  biData,
  className,
  variant = 'ghost',
}) => {
  const { t } = useTranslation();
  const { onEditClick } = useEditDeviationClick(deviation);
  const commonProps = {
    className,
    biData,
    onClick: onEditClick,
    title: t('common.edit'),
  };

  if (variant === 'ghost') {
    return (
      <GhostButton type="button" variant="main" size="medium" {...commonProps}>
        {t('common.edit')}
      </GhostButton>
    );
  } else if (variant === 'text') {
    return (
      <TextButton variant="main" icon={EditIcon} size="medium" {...commonProps}>
        {t('common.edit')}
      </TextButton>
    );
  } else {
    const IconComponent =
      variant === 'icon' ? IconButton : IconButtonWithBackground;
    return (
      <IconComponent
        icon={EditIcon}
        aria-label={t('common.edit')}
        size="medium"
        {...commonProps}
      />
    );
  }
};
EditButton.displayName = 'EditButton';

export default EditButton;
